import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import cn from 'classnames';
import { Box, Button, Callout, Collapse, Column, Row, TextField, Typography, useIsInBottomSheet } from 'seagull';
import { getTextColorsForBackground } from '~/business-logic/contrastLogic';
import { useCobBranding } from '~/client-flow/clientOb/CobBrandingContext';
import { useResponsive } from '~/client-flow/contexts';
import { useIoContext } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import styles from './ConnectCreditCard.module.scss';
import { useConnectCreditCard } from './useConnectCreditCard';
export default function ConnectCreditCard(props) {
    const { t: connectCreditCardTexts } = useLocaleKeys('components.connectCreditCard');
    const { t: managePaymentMethodsTexts } = useLocaleKeys('components.managePaymentMethods');
    const { isProposal } = props;
    const { isMobile } = useResponsive();
    const isInBottomSheet = useIsInBottomSheet();
    const { branding } = useCobBranding();
    const useBrandFocus = isInBottomSheet || !!isProposal;
    const brandButtonStyle = useMemo(()=>{
        if (!useBrandFocus) return undefined;
        const buttonPrimary = branding.buttonPrimary;
        const textOnFilled = getTextColorsForBackground(buttonPrimary).primary;
        return {
            '--button-primary': buttonPrimary,
            '--button-text-on-filled': textOnFilled
        };
    }, [
        useBrandFocus,
        branding.buttonPrimary
    ]);
    const { reactStripe: { CardNumberElement, CardExpiryElement, CardCvcElement } } = useIoContext();
    const { cardNum, cardNumDispatch, expiry, expiryDispatch, cvv, cvvDispatch, nameOnCard, setNameOnCard, zipCode, setZipCode, apiError, setApiError, submitting, allFieldsValid, disabledReason, doPaymentMethodAction, stripeLoadingFailed, styleObj, classesObj, validateNameOnCard, validateZipCode, connectCreditCard } = useConnectCreditCard(props);
    return /*#__PURE__*/ _jsxs(Box, {
        as: "form",
        className: cn(styles.formNewUi, {
            [styles.formNewUiMobile]: isMobile,
            [styles.formInBottomSheet]: useBrandFocus
        }),
        style: brandButtonStyle,
        onSubmit: (e)=>{
            e.preventDefault();
            doPaymentMethodAction(connectCreditCard);
        },
        children: [
            /*#__PURE__*/ _jsx(TextField, {
                value: nameOnCard.value,
                label: isMobile ? connectCreditCardTexts.nameOnCard() : undefined,
                placeholder: isMobile ? connectCreditCardTexts.enterYourName() : connectCreditCardTexts.nameOnCard(),
                inputProps: {
                    'data-testid': 'manage-payment-methods-credit-card-name'
                },
                disabled: !!disabledReason,
                error: nameOnCard.touched && !!nameOnCard.error,
                helperText: nameOnCard.touched && nameOnCard.error ? nameOnCard.error : undefined,
                bi: {
                    name: 'name_on_card'
                },
                onChange: (ev)=>{
                    setApiError(undefined);
                    const error = validateNameOnCard(ev.target.value);
                    setNameOnCard(_object_spread_props(_object_spread({}, nameOnCard), {
                        value: ev.target.value,
                        pristine: false,
                        error
                    }));
                },
                onBlur: ()=>{
                    const error = validateNameOnCard(nameOnCard.value);
                    setNameOnCard(_object_spread_props(_object_spread({}, nameOnCard), {
                        error,
                        touched: true
                    }));
                }
            }),
            /*#__PURE__*/ _jsxs(Column, {
                className: styles.stripeInputGroup,
                children: [
                    isMobile && /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        weight: "normal",
                        skin: "secondary",
                        className: styles.stripeLabel,
                        children: connectCreditCardTexts.cardNumber()
                    }),
                    /*#__PURE__*/ _jsx(CardNumberElement, {
                        className: cn(styles.stripeInput, {
                            [styles.stripeInputInvalid]: cardNum.touched && !cardNum.valid && cardNum.errorMessage,
                            [styles.stripeInputDisabled]: !!disabledReason
                        }),
                        options: {
                            disabled: !!disabledReason,
                            style: styleObj,
                            classes: classesObj,
                            placeholder: connectCreditCardTexts.cardNumber(),
                            disableLink: true
                        },
                        onChange: (event)=>{
                            const { complete, error, empty } = event;
                            setApiError(undefined);
                            if (complete) {
                                cardNumDispatch({
                                    type: 'valid'
                                });
                            } else {
                                cardNumDispatch({
                                    type: 'error',
                                    errorMessage: error ? error.message : empty ? connectCreditCardTexts.invalidCardNumber() : ''
                                });
                            }
                        },
                        onBlur: ()=>cardNumDispatch({
                                type: 'blur'
                            })
                    }),
                    /*#__PURE__*/ _jsx(Collapse, {
                        in: cardNum.touched && !cardNum.valid,
                        unmountOnExit: true,
                        className: styles.collapseWrapper,
                        children: /*#__PURE__*/ _jsx("div", {
                            className: styles.collapseInner,
                            children: /*#__PURE__*/ _jsx(Typography, {
                                as: "p",
                                testId: "connect-cc-num-error",
                                skin: "error",
                                className: styles.helperTextError,
                                children: cardNum.errorMessage || '\u00A0'
                            })
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Row, {
                className: cn(styles.expiryCvvZipRow, {
                    [styles.expiryCvvZipRowMobile]: isMobile
                }),
                children: [
                    /*#__PURE__*/ _jsxs(Box, {
                        className: cn(styles.expiryCvvWrapper, {
                            [styles.expiryCvvWrapperMobile]: isMobile
                        }),
                        children: [
                            /*#__PURE__*/ _jsxs(Column, {
                                className: styles.expiryCvvZipField,
                                children: [
                                    isMobile && /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph2",
                                        weight: "normal",
                                        skin: "secondary",
                                        className: styles.stripeLabel,
                                        children: connectCreditCardTexts.expirationDate()
                                    }),
                                    /*#__PURE__*/ _jsx(CardExpiryElement, {
                                        "data-testid": "manage-payment-methods-credit-card-expiry",
                                        className: cn(styles.stripeInput, {
                                            [styles.stripeInputInvalid]: expiry.touched && expiry.errorMessage,
                                            [styles.stripeInputDisabled]: !!disabledReason
                                        }),
                                        options: {
                                            disabled: !!disabledReason,
                                            style: styleObj,
                                            classes: classesObj,
                                            placeholder: 'MM/YY'
                                        },
                                        onChange: (event)=>{
                                            const { complete, error, empty } = event;
                                            setApiError(undefined);
                                            if (complete) {
                                                expiryDispatch({
                                                    type: 'valid'
                                                });
                                            } else {
                                                expiryDispatch({
                                                    type: 'error',
                                                    errorMessage: error ? error.message : empty ? connectCreditCardTexts.invalidExpiryDate() : ''
                                                });
                                            }
                                        },
                                        onBlur: ()=>expiryDispatch({
                                                type: 'blur'
                                            })
                                    }),
                                    /*#__PURE__*/ _jsx(Collapse, {
                                        in: expiry.touched && !expiry.valid,
                                        unmountOnExit: true,
                                        className: styles.collapseWrapper,
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: styles.collapseInner,
                                            children: /*#__PURE__*/ _jsx(Typography, {
                                                as: "p",
                                                testId: "connect-cc-expiry-error",
                                                skin: "error",
                                                className: styles.helperTextError,
                                                children: expiry.errorMessage || '\u00A0'
                                            })
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Column, {
                                className: styles.expiryCvvZipField,
                                children: [
                                    isMobile && /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph2",
                                        weight: "normal",
                                        skin: "secondary",
                                        className: styles.stripeLabel,
                                        children: connectCreditCardTexts.cvv()
                                    }),
                                    /*#__PURE__*/ _jsx(CardCvcElement, {
                                        className: cn(styles.stripeInput, {
                                            [styles.stripeInputInvalid]: cvv.touched && cvv.errorMessage,
                                            [styles.stripeInputDisabled]: !!disabledReason
                                        }),
                                        options: {
                                            disabled: !!disabledReason,
                                            style: styleObj,
                                            classes: classesObj,
                                            placeholder: connectCreditCardTexts.cvv()
                                        },
                                        onChange: (event)=>{
                                            const { complete, error, empty } = event;
                                            setApiError(undefined);
                                            if (complete) {
                                                cvvDispatch({
                                                    type: 'valid'
                                                });
                                            } else {
                                                cvvDispatch({
                                                    type: 'error',
                                                    errorMessage: error ? error.message : empty ? connectCreditCardTexts.invalidCvv() : ''
                                                });
                                            }
                                        },
                                        onBlur: ()=>cvvDispatch({
                                                type: 'blur'
                                            })
                                    }),
                                    /*#__PURE__*/ _jsx(Collapse, {
                                        in: cvv.touched && !cvv.valid,
                                        unmountOnExit: true,
                                        className: styles.collapseWrapper,
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: styles.collapseInner,
                                            children: /*#__PURE__*/ _jsx(Typography, {
                                                as: "p",
                                                testId: "connect-cc-cvv-error",
                                                skin: "error",
                                                className: styles.helperTextError,
                                                children: cvv.errorMessage || '\u00A0'
                                            })
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Box, {
                        className: cn(styles.expiryCvvZipField, styles.zipCodeField),
                        children: /*#__PURE__*/ _jsx(TextField, {
                            value: zipCode.value,
                            label: isMobile ? connectCreditCardTexts.zipCode() : undefined,
                            placeholder: connectCreditCardTexts.zipCode(),
                            inputProps: {
                                'data-testid': 'connect-cc-zip'
                            },
                            disabled: !!disabledReason,
                            error: zipCode.touched && !!zipCode.error,
                            helperText: zipCode.touched && zipCode.error ? zipCode.error : undefined,
                            bi: {
                                name: 'zip_code'
                            },
                            onChange: (ev)=>{
                                setApiError(undefined);
                                const value = ev.target.value.trim();
                                const error = validateZipCode(value, -1);
                                setZipCode(_object_spread_props(_object_spread({}, zipCode), {
                                    value,
                                    pristine: false,
                                    error
                                }));
                            },
                            onBlur: ()=>{
                                const error = validateZipCode(zipCode.value, -1);
                                setZipCode(_object_spread_props(_object_spread({}, zipCode), {
                                    touched: true,
                                    error
                                }));
                            }
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Column, {
                className: styles.buttonSection,
                children: [
                    /*#__PURE__*/ _jsxs(Row, {
                        className: cn(styles.buttonRowNewUi, {
                            [styles.buttonRowNewUiMobile]: isMobile
                        }),
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 16,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                skin: "standard",
                                className: isMobile ? styles.feeDisclaimerMobile : undefined,
                                children: connectCreditCardTexts.feeDisclaimer()
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                className: styles.buttonRowNewUiButton,
                                type: "submit",
                                size: "small",
                                skin: "primary",
                                roundness: "m",
                                fluid: isMobile,
                                disabled: !!(!allFieldsValid || apiError || stripeLoadingFailed),
                                loading: submitting,
                                testId: "manage-payment-methods-add-credit-card",
                                intent: Intent.AddPaymentMethod,
                                bi: {
                                    name: 'connect_credit_card'
                                },
                                tooltip: {
                                    placement: 'top',
                                    title: disabledReason ? /*#__PURE__*/ _jsx("span", {
                                        "data-testid": "add-cc-disabled-reason",
                                        children: disabledReason
                                    }) : '',
                                    testId: 'connect-cc-submit-btn-tooltip',
                                    disableFocusListener: disabledReason == null,
                                    disableHoverListener: disabledReason == null,
                                    disableTouchListener: disabledReason == null
                                },
                                children: managePaymentMethodsTexts.connectCard()
                            })
                        ]
                    }),
                    (apiError || stripeLoadingFailed) && /*#__PURE__*/ _jsx(Callout, {
                        intent: "error",
                        testId: "manage-payment-methods-cc-error",
                        children: stripeLoadingFailed ? managePaymentMethodsTexts.anErrorOccurred() : apiError
                    })
                ]
            })
        ]
    });
}
